import React, { Component } from 'react';
import { Link, Navigate } from 'react-router-dom';
import CartPageItem from '../../components/CartPageItem/CartPageItem';
import { CartPageItemType } from '../../components/CartPageItem/CartPageItem.props';
import ChoiceMessage from '../../components/ChoiceMessage/ChoiceMessage';
import FormFieldChooseGroup from '../../components/FormFieldChooseGroup/FormFieldChooseGroup';
import HeaderShop from '../../components/HeaderShop/HeaderShop';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import ResultPriceWithFormElement from '../../components/ResultPriceWithFormElement/ResultPriceWithFormElement';
import { CustomerDataFields, CustomerDataOptionsPayTyp } from '../../interfaces/CustomerData/CustomerData';
import { GlobalContext } from '../../interfaces/GlobalContext/GlobalContext';
import { OrderData, OrderDataFields } from '../../interfaces/OrderData/OrderData';
import ProductPrice from '../../interfaces/ProductPrice/ProductPrice';
import ProductProperties, { FormFieldName } from '../../interfaces/ProductProperties/ProductProperties';
import { ProductType } from '../../interfaces/ProductProperties/ProductPropertiesRelated';
import { UrlPaths } from '../../interfaces/UrlPaths/UrlPaths';
import CheckDataPageProps from './CheckDataPage.props';
import CheckDataPageState from './CheckDataPage.state';

export default class CheckDataPage extends Component<CheckDataPageProps, CheckDataPageState> {
    static contextType = GlobalContext;
    context!: React.ContextType<typeof GlobalContext>;

    constructor(props: CheckDataPageProps) {
        super(props);
        this.state = {
            prices: {},
            orderData: {},
            plusOption: false
        };

        this.refreshPage = this.refreshPage.bind(this);
        this.updatePrices = this.updatePrices.bind(this);
        this.setPlusOtion = this.setPlusOtion.bind(this);
        this.getOrderData = this.getOrderData.bind(this);
        this.getCartData = this.getCartData.bind(this);
        this.clearDirectPurchaseData = this.clearDirectPurchaseData.bind(this);
    }

    refreshPage() {
        this.forceUpdate();
    }

    componentDidMount() {
        this.getOrderData();
        
        // Set up cleanup listeners for direct purchase
        const { isDirectPurchase } = this.getCartData();
        if (isDirectPurchase) {
            this.setupDirectPurchaseCleanup();
        }
        
        window.scrollTo(0, 0);
    }

    componentWillUnmount() {
        // Clean up event listeners
        window.removeEventListener('beforeunload', this.clearDirectPurchaseData);
    }

    setupDirectPurchaseCleanup() {
        // Clear on page unload
        window.addEventListener('beforeunload', this.clearDirectPurchaseData);
    }

    clearDirectPurchaseData() {
        this.context.sessionStorageProvider.clearDirectPurchaseFromSessionStorage();
    }

    updatePrices(pProductPrice: ProductPrice) {
        const prices = this.state.prices;
        
        if (pProductPrice.price === 0) {
            delete prices[pProductPrice.magicNumber as string];
        } else {
            prices[pProductPrice.magicNumber as string] = pProductPrice;
        }

        this.setState({
            prices: prices
        });
    }

    // Method to determine cart data source - supports both direct purchase and regular cart
    getCartData(): { warenkorbData: ProductProperties[], isDirectPurchase: boolean, campaignId?: string } {
        // Check for direct purchase first
        const directPurchaseData = this.context.sessionStorageProvider.getDirectPurchaseFromSessionStorage();
        if (directPurchaseData) {
            return {
                warenkorbData: [directPurchaseData.productProperties],
                isDirectPurchase: true,
                campaignId: directPurchaseData.campaignId
            };
        }
        
        // Fall back to regular cart
        const regularCart = this.context.localStorageProvider.getProductInformationFromLocalStorage();
        return {
            warenkorbData: regularCart?.warenkorbData || [],
            isDirectPurchase: false,
            campaignId: undefined
        };
    }

    setPlusOtion(value?: string, e?: React.ChangeEvent<HTMLInputElement>) {
        this.setState({
            plusOption: e?.target.checked || false
        }, () => this.getOrderData());
    }

    //OMPRO-1943 Cookie auslesen in dem ggf eine Kampagnen-Nummer steht
    getCookieWithCampaignId() {
        //KA auf Testsystem: KA13846 || 4186
        function getCookie(name: string): string | undefined {
            const value = `; ${document.cookie}`;
            const parts: string[] = value.split(`; ${name}=`);
            if (parts.length === 2) {
                const lastPart = parts.pop();
                if (lastPart) {
                    return lastPart.split(';').shift();
                }
            }
            return undefined;
        }
        const campaignId = getCookie("campaign");

        // Validierung: Beginnt mit "KA", danach nur Ziffern, Gesamtlänge 7–10 Zeichen
        if (
            campaignId &&
            campaignId.startsWith("KA") &&
            /^\d{5}$/.test(campaignId.slice(2)) &&
            campaignId.length >= 7 &&
            campaignId.length <= 10
        ) {
            return campaignId;
        }

        return undefined;
    }

    getOrderData() {
        const campaignIdFromCookie = this.getCookieWithCampaignId();       
        const campaignIdFromLocalStorage = this.context.localStorageProvider.getCampaignFromLocalStorage();
        const campaignIdFromSessionStorage = this.context.sessionStorageProvider.getCampaignFromSessionStorage();
        const { warenkorbData, campaignId: directPurchaseCampaignId } = this.getCartData();
        const customerData = this.context.localStorageProvider.getCustomerDataFromLocalStorage();
        const plusOption = this.state.plusOption;
        const captchaToken = this.context.localStorageProvider.getCaptchaTokenFromLocalStorage();

        // Priority: direct purchase campaign → session → local → cookie
        const finalCampaignId = directPurchaseCampaignId || campaignIdFromSessionStorage || campaignIdFromLocalStorage || campaignIdFromCookie;

        // console.log('campaign-check',
        //     'session:', campaignIdFromSessionStorage, 
        //     'localstorage:',campaignIdFromLocalStorage,
        //     'cookie:',campaignIdFromCookie,
        //     'directPurchase:',directPurchaseCampaignId,
        //     'final:', finalCampaignId);

        const mergeOrderData: OrderData = {};
        mergeOrderData.products = this.context.utilityProvider.getOrderDataProductsFromProductPropertiesArray(warenkorbData || [], finalCampaignId);
        mergeOrderData.customer = customerData;
        mergeOrderData.plusOption = plusOption;
        mergeOrderData.captchaToken = captchaToken;

        this.setState({
            orderData: mergeOrderData
        });
    }

    checkForPlusOptions() {
        let status = false;
        const { warenkorbData } = this.getCartData();
        warenkorbData?.forEach((item) => {
            if (item.productType !== ProductType.TEST) {
                status = true;
                return;
            }
        });
        return status;

    }

    render(): React.ReactNode {
        const { warenkorbData, isDirectPurchase } = this.getCartData();
        
        if (!warenkorbData.length) {
            return <Navigate to={UrlPaths.CARTPAGE} />
        }

        if (!this.context.localStorageProvider.getCustomerDataFromLocalStorage().email) {
            return <Navigate to={UrlPaths.CUSTOMERDATAPAGE} />
        }

        return (
            <>
                <HeaderShop />
                <ProgressBar steps={(() => {
                    const steps = this.context.i18nProvider.get(
                        isDirectPurchase ? 'progressBar.directPurchase.steps' : 'progressBar.steps', 
                        { returnObjects: true }
                    ) as unknown as Array<string>;
                    return Array.isArray(steps) ? steps : (this.context.i18nProvider.get('progressBar.steps', { returnObjects: true }) as unknown as Array<string> || []);
                })()} 
                activeStep={2} />
                <section className="cs-check-data-page content-section" data-testid="CheckDataPage">
                    <div className="container">

                        <div className="check-data-page">
                            <div className="check-data-page__container">
                                <h1 className="check-data-page__title">{ this.context.i18nProvider.get("checkDataPage.pageTitle") }</h1>
                                <div className="check-data-page__details-wrapper">
                                    <div className="check-data-page__payment-details-col col-md-5">
                                        <div className="check-data-page__payment-details">
                                            <div className="check-data-page__customer-address">
                                                <div className="check-data-page__customer-address-name">
                                                    <span>{this.context.i18nProvider.get("customerDataPage.customerDataFields." + CustomerDataFields.FORMOFADDRESS + ".options." + this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.FORMOFADDRESS])} </span>
                                                    <span>{this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.PRENAME]} {this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.SURNAME]}</span>
                                                </div>
                                                <span>{this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.COMPANYNAME]}</span>
                                                <span>{this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.STREET]} {this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.STREETNUMBER]}</span>
                                                <span>{this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.PLZ]} {this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.CITY]}</span>
                                            </div>
                                            <div className="check-data-page__customer-contact-info">
                                                {
                                                    !(!this.context.loginProvider.hasJWTCookie() 
                                                        && !this.context.utilityProvider.orderHasBuyableProduct(warenkorbData || []))
                                                        && <div className="check-data-page__customer-contact-info-username">
                                                            <span>{ this.context.i18nProvider.get("checkDataPage.username") } </span>
                                                            <span>{this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.LOGIN]}</span>
                                                        </div>
                                                }
                                                <div className="check-data-page__customer-contact-info-phone-mail">
                                                    <span className="check-data-page__customer-contact-info-phone">{ this.context.i18nProvider.get("checkDataPage.phone") } </span>
                                                    <span className="check-data-page__customer-contact-info-phone-number">{this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.PHONENUMBER]}</span>
                                                    <span className="check-data-page__customer-contact-info-mail">{ this.context.i18nProvider.get("checkDataPage.mail") } </span>
                                                    <div className="check-data-page__customer-contact-info-email-details">
                                                        <span>{this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.MAIL]}</span>
                                                        {
                                                            this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.MAIL] === this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.BILLINGMAIL] ? <>
                                                                <span> { this.context.i18nProvider.get("checkDataPage.mailComment") }</span>
                                                            </> : <></>
                                                        }
                                                    </div>
                                                    {
                                                        this.context.utilityProvider.orderHasBuyableProduct(
                                                            warenkorbData || []
                                                        ) && this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.MAIL] !== this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.BILLINGMAIL] 
                                                            ? <>
                                                                <span className="check-data-page__customer-contact-info-billingMail">{ this.context.i18nProvider.get("checkDataPage.billingMail") } </span>
                                                                <div className="check-data-page__customer-contact-info-billingEmail-details">
                                                                    <span>{this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.BILLINGMAIL]}</span>
                                                                </div>
                                                            </> 
                                                            : <></>
                                                    }
                                                </div>
                                            </div>
                                            {
                                                this.context.utilityProvider.orderHasBuyableProduct(
                                                    warenkorbData || []
                                                ) && <div className="check-data-page__contract-details">
                                                    {
                                                        this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.PAYTYP] === CustomerDataOptionsPayTyp.DEBIT ? <>
                                                            <span>{ this.context.i18nProvider.get("checkDataPage.paymentType.debit") }</span>
                                                            <span>{this.context.localStorageProvider.getCustomerDataFromLocalStorage()[CustomerDataFields.IBAN]}</span>
                                                        </> : <>
                                                            <span>{ this.context.i18nProvider.get("checkDataPage.paymentType.billing") }</span>
                                                        </>
                                                    }
                                                </div>
                                            }
                                            {!this.context.loginProvider.hasJWTCookie() && 
                                                <Link to={UrlPaths.CUSTOMERDATAPAGE}
                                                    className='text-icon-link text-icon-link--button text-icon-link--edit'>
                                                    <span className="text-icon-link__icon"></span>
                                                    <span className="text-icon-link__text">{ this.context.i18nProvider.get("checkDataPage.changeDataLabel") }</span>
                                                </Link>
                                            }
                                        </div>
                                    </div>
                                    <div className="check-data-page__products-overview-col col-md-7">
                                        <div className="check-data-page__products-overview">
                                            {warenkorbData?.length !== 0 ? <>
                                                <div className="cart-page">
                                                    <div className="cart-page__container">
                                                        <ul className="cart-page__products">
                                                            {warenkorbData?.map(
                                                                (productProperties: ProductProperties) => {
                                                                    return <CartPageItem productProperties={productProperties}
                                                                        key={productProperties.magicNumber}
                                                                        updateHandler={this.refreshPage}
                                                                        updatePrices={this.updatePrices}
                                                                        cartPage={false} 
                                                                        isDirectPurchase={isDirectPurchase}
                                                                        license={productProperties.configOptions?.find(productOption => productOption.name === FormFieldName.NUMBEROFLICENCES)?.value}
                                                                        type={
                                                                            productProperties.productType.toLocaleLowerCase() === CartPageItemType.TEST
                                                                                ? CartPageItemType.TEST
                                                                                : CartPageItemType.PRODUCT
                                                                        }/>
                                                                }
                                                            )}
                                                            {
                                                                warenkorbData.length ? <>
                                                                    <CartPageItem productProperties={{
                                                                        configOptions: [],
                                                                        magicNumber: "",
                                                                        productType: ProductType.BASE,
                                                                        professionalContract: false,
                                                                        relatedProducts: [],
                                                                        queryInformation: {
                                                                            imageSrc: this.context.i18nProvider.get('cartpageServiceItem.imageSrc') || "",
                                                                            imageAlt: this.context.i18nProvider.get('cartpageServiceItem.imageAlt') || "",
                                                                            title: this.context.i18nProvider.get('cartpageServiceItem.title') || "",
                                                                            href: undefined,
                                                                        }
                                                                    }}
                                                                    cartPage={true} 
                                                                    type={CartPageItemType.SERVICE} />
                                                                </> : <></>
                                                            }
                                                        </ul>
                                                    </div>
                                                </div>
                                            </> : <></>}
                                        </div>
                                    </div>
                                </div>
                                { this.checkForPlusOptions() &&
                                    <div className="check-data-page__plus-option col-lg-7">
                                        <FormFieldChooseGroup id={OrderDataFields.PLUSOPTION}
                                            options={[
                                                {
                                                    label: this.context.i18nProvider.get('checkDataPage.plusOption') || "",
                                                    value: 'true'
                                                }
                                            ]}
                                            required={false}
                                            optionStyle={'checkbox'}
                                            onChange={(value, e) => this.setPlusOtion(value, e)} />
                                    </div>
                                }
                                <ResultPriceWithFormElement prices={{...this.state.prices}} orderData={this.state.orderData} />
                                <ChoiceMessage />
                            </div>
                        </div>

                    </div>
                </section>
            </>
        );
    }
}
